// Shared authed-fetch helper for Lead Command Center panels.
//
// Bug this fixes: every LeadCC panel independently called
// supabase.auth.getSession() at fetch time to build the Authorization
// header. When the persisted JWT is stale-but-not-expired, Supabase
// tries to refresh it — if that refresh hangs (upstream network
// issue, edge outage, whatever), getSession() NEVER resolves.
// Component's fetch never fires because it's awaiting bearer().
// state.loading stays true forever. Panel returns null forever.
//
// This is the SAME class of bug as the boot-time hang I fixed with
// sessionPreflight + auth watchdog — but scoped to per-component
// fetches instead of the initial hydration. The preflight only
// runs at boot; a session that goes stale after boot can still hang
// subsequent getSession() calls.
//
// Belt + suspenders:
//   1. bearer()  — 2s watchdog around getSession(). If it hangs,
//                  we proceed without an Authorization header and
//                  the endpoint returns 401. The 401 becomes a
//                  visible error state on the panel (amber "AI
//                  briefing temporarily unavailable"), NOT an
//                  infinite loading state.
//   2. fetch    — AbortController with 10s timeout. If the endpoint
//                  itself hangs (Supabase issue, cold cron, etc.),
//                  we abort and set state.error. Panel shows the
//                  amber fallback.
//
// Every LeadCC panel uses this so the failure modes are uniform.

// No supabase import — bearerHeader() reads localStorage directly to
// avoid triggering Supabase's refresh flow.
const FETCH_TIMEOUT_MS = 10_000;

// Decode a JWT payload without verifying signature (we only need
// the exp claim). Same helper as sessionPreflight.js — inlined here
// so authedFetch is standalone.
function decodeJwtExp(jwt) {
  try {
    const parts = String(jwt).split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/').padEnd(parts[1].length + (4 - parts[1].length % 4) % 4, '=');
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch { return null; }
}

// Read the persisted Supabase session directly from localStorage.
// SIDESTEPS supabase.auth.getSession() entirely — that call triggers
// an internal refresh flow that hangs when the refresh token is bad,
// which is the exact bug this file was built to work around.
//
// If the persisted access_token's exp is past (or within 60s of),
// we return null → no Authorization header → endpoint 401 → visible
// amber "temporarily unavailable" fallback. Better than hanging.
export function readPersistedJwtDirect() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const now = Math.floor(Date.now() / 1000);
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (!(k.startsWith('sb-') && k.endsWith('-auth-token'))) continue;
      const raw = window.localStorage.getItem(k);
      if (!raw) continue;
      let parsed;
      try { parsed = JSON.parse(raw); } catch { continue; }
      const session = parsed?.currentSession
                   || (Array.isArray(parsed) ? { access_token: parsed[0] } : parsed);
      const token = session?.access_token;
      if (!token) continue;
      const exp = decodeJwtExp(token);
      if (exp == null) continue;
      // Reject if expired or within 60s safety margin.
      if (exp <= now + 60) continue;
      return token;
    }
  } catch { /* localStorage disabled or other */ }
  return null;
}

async function bearerHeader() {
  // Read persisted token directly — no async getSession() call, no
  // refresh loop possible. If the local token is fresh, we send it.
  // If it's expired/missing, we send nothing (401 → visible amber
  // fallback). Never hangs.
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * POST JSON to a lead endpoint with auth header + timeout guard.
 * Returns { ok, data, error, status }. Never throws.
 *
 * @param {string} url    — /api/leads/... path
 * @param {object} body   — request body (JSON-serializable)
 * @param {AbortSignal}   — optional external signal (e.g. useEffect cleanup)
 */
export async function authedFetchLead(url, body, externalSignal) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  // Chain the external signal (useEffect cleanup) so unmount cancels
  // the in-flight fetch even before the 10s ceiling.
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  try {
    const headers = { 'Content-Type': 'application/json', ...(await bearerHeader()) };
    const r = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    let data = null;
    try { data = await r.json(); } catch { /* non-JSON response */ }
    if (!r.ok) {
      return {
        ok: false,
        data: null,
        error: data?.error || `HTTP ${r.status}`,
        status: r.status,
      };
    }
    if (data && data.ok === false) {
      return { ok: false, data: null, error: data.error || 'response ok:false', status: r.status };
    }
    return { ok: true, data, error: null, status: r.status };
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      return { ok: false, data: null, error: `timeout after ${FETCH_TIMEOUT_MS / 1000}s`, status: 0 };
    }
    return { ok: false, data: null, error: err.message || String(err), status: 0 };
  }
}
